/**
 * Health Data Transmission Service
 * Collects health data from Apple Health and FHIR wallet, then transmits to external endpoint
 */

import { AppleHealthService, HealthDataUpdate } from './appleHealthService';
import { loadFHIRData } from '../utils/fhirDataUtils';

export interface CollectedHealthData {
  persona: string;
  timestamp: string;
  data: {
    appleHealth?: {
      steps?: {
        current: number;
        unit: string;
        lastUpdated: string;
      };
      sleep?: {
        value: string;
        lastUpdated: string;
      };
      [key: string]: any;
    };
    wallet?: {
      demographics?: any;
      conditions?: any[];
      medications?: any[];
      immunizations?: any[];
      allergies?: any[];
      procedures?: any[];
      providers?: any[];
      vitals?: any[];
      [key: string]: any;
    };
  };
  metadata: {
    dataSource: string;
    version: string;
    lastUpdated: string;
    hasWalletData: boolean;
    hasAppleHealthData: boolean;
    appleHealthDataTypes: string[];
  };
  dataSources: {
    wallet: boolean;
    appleHealth: boolean;
  };
}

export class HealthDataTransmissionService {
  private apiEndpoint = 'https://qr.md/r/wallet/fhir/?t=';
  private appleHealthService: AppleHealthService | null = null;
  private collectedAppleHealthData: Map<string, HealthDataUpdate> = new Map();

  constructor(appleHealthService?: AppleHealthService) {
    if (appleHealthService) {
      this.appleHealthService = appleHealthService;
    }
  }

  /**
   * Collects all available health data from Apple Health and FHIR wallet
   */
  async collectHealthData(persona: string = 'me'): Promise<CollectedHealthData> {
    try {
      console.log('[HealthDataTransmission] Collecting health data for persona:', persona);

      // Collect Apple Health data
      let appleHealthData: any = null;
      if (this.appleHealthService && this.appleHealthService.getIsAvailable() && this.appleHealthService.getHasPermissions()) {
        try {
          appleHealthData = await this.collectAppleHealthData();
          console.log('[HealthDataTransmission] Apple Health data collected');
        } catch (error) {
          console.warn('[HealthDataTransmission] Failed to collect Apple Health data:', error);
        }
      }

      // Collect wallet/FHIR data
      let walletData: any = null;
      try {
        const fhirBundle = await loadFHIRData(persona);
        if (fhirBundle && fhirBundle.entry && fhirBundle.entry.length > 0) {
          walletData = this.processFHIRBundle(fhirBundle);
          console.log('[HealthDataTransmission] Wallet/FHIR data collected');
        }
      } catch (error) {
        console.warn('[HealthDataTransmission] Failed to collect wallet data:', error);
      }

      // Build the collected data object
      const collectedData: CollectedHealthData = {
        persona,
        timestamp: new Date().toISOString(),
        data: {
          ...(appleHealthData ? { appleHealth: appleHealthData } : {}),
          ...(walletData ? { wallet: walletData } : {}),
        },
        metadata: {
          dataSource: 'health-wallet-and-apple-health',
          version: '2.0',
          lastUpdated: new Date().toISOString(),
          hasWalletData: !!walletData,
          hasAppleHealthData: !!appleHealthData,
          appleHealthDataTypes: appleHealthData ? Object.keys(appleHealthData) : [],
        },
        dataSources: {
          wallet: !!walletData,
          appleHealth: !!appleHealthData,
        },
      };

      console.log('[HealthDataTransmission] Health data collected successfully');
      return collectedData;
    } catch (error) {
      console.error('[HealthDataTransmission] Error collecting health data:', error);
      throw error;
    }
  }

  /**
   * Collects Apple Health data (steps, sleep, heart rate, oxygen saturation, etc.)
   */
  private async collectAppleHealthData(): Promise<any> {
    if (!this.appleHealthService || !this.appleHealthService.getIsAvailable()) {
      return null;
    }

    const healthData: any = {};

    // Get current step count from collected updates
    const stepUpdate = this.collectedAppleHealthData.get('steps');
    if (stepUpdate) {
      healthData.steps = {
        current: typeof stepUpdate.value === 'number' ? stepUpdate.value : 0,
        unit: stepUpdate.unit || 'steps',
        lastUpdated: stepUpdate.timestamp,
      };
    }

    // Get sleep data if available
    const sleepUpdate = this.collectedAppleHealthData.get('sleep');
    if (sleepUpdate) {
      healthData.sleep = {
        value: String(sleepUpdate.value),
        unit: sleepUpdate.unit || 'hours',
        lastUpdated: sleepUpdate.timestamp,
      };
    }

    // Get heart rate data if available
    const heartRateUpdate = this.collectedAppleHealthData.get('heartRate');
    if (heartRateUpdate) {
      healthData.heartRate = {
        value: typeof heartRateUpdate.value === 'number' ? heartRateUpdate.value : 0,
        unit: heartRateUpdate.unit || 'bpm',
        lastUpdated: heartRateUpdate.timestamp,
      };
    }

    // Get oxygen saturation data if available
    const oxygenUpdate = this.collectedAppleHealthData.get('oxygenSaturation');
    if (oxygenUpdate) {
      healthData.oxygenSaturation = {
        value: typeof oxygenUpdate.value === 'number' ? oxygenUpdate.value : 0,
        unit: oxygenUpdate.unit || '%',
        lastUpdated: oxygenUpdate.timestamp,
      };
    }

    // Get atrial fibrillation burden data if available
    const afibBurdenUpdate = this.collectedAppleHealthData.get('atrialFibrillationBurden');
    if (afibBurdenUpdate) {
      healthData.atrialFibrillationBurden = {
        value: typeof afibBurdenUpdate.value === 'number' ? afibBurdenUpdate.value : 0,
        unit: afibBurdenUpdate.unit || '%',
        lastUpdated: afibBurdenUpdate.timestamp,
      };
    }

    return Object.keys(healthData).length > 0 ? healthData : null;
  }

  /**
   * Processes FHIR bundle into structured wallet data
   */
  private processFHIRBundle(bundle: any): any {
    if (!bundle || !bundle.entry) {
      return null;
    }

    const walletData: any = {
      demographics: null,
      conditions: [],
      medications: [],
      immunizations: [],
      allergies: [],
      procedures: [],
      providers: [],
      vitals: [],
    };

    // Extract resources by type
    bundle.entry.forEach((entry: any) => {
      if (!entry.resource) return;

      const resource = entry.resource;
      const resourceType = resource.resourceType;

      switch (resourceType) {
        case 'Patient':
          walletData.demographics = this.extractDemographics(resource);
          break;
        case 'Condition':
          walletData.conditions.push(this.extractCondition(resource));
          break;
        case 'MedicationRequest':
        case 'MedicationStatement':
          walletData.medications.push(this.extractMedication(resource));
          break;
        case 'Immunization':
          walletData.immunizations.push(this.extractImmunization(resource));
          break;
        case 'AllergyIntolerance':
          walletData.allergies.push(this.extractAllergy(resource));
          break;
        case 'Procedure':
          walletData.procedures.push(this.extractProcedure(resource));
          break;
        case 'Practitioner':
        case 'PractitionerRole':
          walletData.providers.push(this.extractProvider(resource));
          break;
        case 'Observation':
          if (resource.category?.[0]?.coding?.[0]?.code === 'vital-signs') {
            walletData.vitals.push(this.extractVital(resource));
          }
          break;
      }
    });

    return walletData;
  }

  /**
   * Extract demographic information from Patient resource
   */
  private extractDemographics(patient: any): any {
    return {
      name: patient.name?.[0] ? {
        given: patient.name[0].given || [],
        family: patient.name[0].family || '',
        full: `${(patient.name[0].given || []).join(' ')} ${patient.name[0].family || ''}`.trim(),
      } : null,
      birthDate: patient.birthDate,
      gender: patient.gender,
      address: patient.address?.[0] ? {
        line: patient.address[0].line || [],
        city: patient.address[0].city || '',
        state: patient.address[0].state || '',
        postalCode: patient.address[0].postalCode || '',
        country: patient.address[0].country || '',
      } : null,
      telecom: patient.telecom || [],
    };
  }

  /**
   * Extract condition information
   */
  private extractCondition(condition: any): any {
    return {
      code: condition.code?.coding?.[0]?.display || condition.code?.text,
      clinicalStatus: condition.clinicalStatus?.coding?.[0]?.code,
      verificationStatus: condition.verificationStatus?.coding?.[0]?.code,
      onsetDateTime: condition.onsetDateTime,
      abatementDateTime: condition.abatementDateTime,
    };
  }

  /**
   * Extract medication information
   */
  private extractMedication(medication: any): any {
    return {
      medication: medication.medicationCodeableConcept?.coding?.[0]?.display || medication.medicationCodeableConcept?.text,
      status: medication.status,
      dosage: medication.dosage?.[0]?.text,
      startDate: medication.effectiveDateTime || medication.effectivePeriod?.start,
    };
  }

  /**
   * Extract immunization information
   */
  private extractImmunization(immunization: any): any {
    return {
      vaccine: immunization.vaccineCode?.coding?.[0]?.display || immunization.vaccineCode?.text,
      status: immunization.status,
      date: immunization.occurrenceDateTime,
    };
  }

  /**
   * Extract allergy information
   */
  private extractAllergy(allergy: any): any {
    return {
      substance: allergy.code?.coding?.[0]?.display || allergy.code?.text,
      category: allergy.category?.[0],
      criticality: allergy.criticality,
    };
  }

  /**
   * Extract procedure information
   */
  private extractProcedure(procedure: any): any {
    return {
      code: procedure.code?.coding?.[0]?.display || procedure.code?.text,
      status: procedure.status,
      performedDateTime: procedure.performedDateTime,
    };
  }

  /**
   * Extract provider information
   */
  private extractProvider(provider: any): any {
    return {
      name: provider.name ? {
        given: provider.name.given || [],
        family: provider.name.family || '',
        full: `${(provider.name.given || []).join(' ')} ${provider.name.family || ''}`.trim(),
      } : null,
      specialty: provider.qualification?.[0]?.code?.coding?.[0]?.display,
    };
  }

  /**
   * Extract vital sign information
   */
  private extractVital(observation: any): any {
    return {
      code: observation.code?.coding?.[0]?.display || observation.code?.text,
      value: observation.valueQuantity?.value,
      unit: observation.valueQuantity?.unit,
      date: observation.effectiveDateTime,
    };
  }

  /**
   * Records an Apple Health data update for collection
   */
  recordAppleHealthUpdate(update: HealthDataUpdate): void {
    this.collectedAppleHealthData.set(update.type, update);
  }

  /**
   * Transmits health data to the external endpoint
   */
  async transmitHealthData(healthData: CollectedHealthData, token?: string): Promise<any> {
    try {
      // Build the URL with token parameter if provided
      const url = token ? `${this.apiEndpoint}${token}` : this.apiEndpoint;

      console.log('[HealthDataTransmission] ========================================');
      console.log('[HealthDataTransmission] 🚀 TRANSMITTING HEALTH DATA');
      console.log('[HealthDataTransmission] Endpoint:', url);
      console.log('[HealthDataTransmission] Data summary:', {
        persona: healthData.persona,
        timestamp: healthData.timestamp,
        hasAppleHealth: !!healthData.data.appleHealth,
        hasWallet: !!healthData.data.wallet,
        appleHealthTypes: healthData.metadata.appleHealthDataTypes,
      });
      console.log('[HealthDataTransmission] Full data:', JSON.stringify(healthData, null, 2));
      console.log('[HealthDataTransmission] ========================================');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(healthData),
      });

      console.log('[HealthDataTransmission] Response status:', response.status);
      console.log('[HealthDataTransmission] Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[HealthDataTransmission] ❌ HTTP error response:', errorText);
        throw new Error(`Server responded with status: ${response.status}. ${errorText}`);
      }

      const result = await response.json().catch(() => {
        const text = response.text();
        console.log('[HealthDataTransmission] Response is not JSON, raw text:', text);
        return {};
      });
      
      console.log('[HealthDataTransmission] ✅ Health data transmitted successfully');
      console.log('[HealthDataTransmission] Response data:', result);
      console.log('[HealthDataTransmission] ========================================');
      return result;
    } catch (error) {
      console.error('[HealthDataTransmission] ❌ Error transmitting health data:', error);
      console.error('[HealthDataTransmission] Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      console.log('[HealthDataTransmission] ========================================');
      throw error;
    }
  }

  /**
   * Collects and transmits health data in one call
   */
  async collectAndTransmitHealthData(persona: string = 'me', token?: string): Promise<any> {
    console.log('[HealthDataTransmission] ========================================');
    console.log('[HealthDataTransmission] Starting collection and transmission...');
    console.log('[HealthDataTransmission] Persona:', persona);
    console.log('[HealthDataTransmission] Token:', token ? 'provided' : 'not provided');
    
    const healthData = await this.collectHealthData(persona);
    
    console.log('[HealthDataTransmission] Collected data summary:');
    console.log('[HealthDataTransmission] - Has Apple Health data:', !!healthData.data.appleHealth);
    console.log('[HealthDataTransmission] - Has Wallet data:', !!healthData.data.wallet);
    console.log('[HealthDataTransmission] - Apple Health types:', healthData.metadata.appleHealthDataTypes);
    
    if (!healthData.data.appleHealth && !healthData.data.wallet) {
      console.warn('[HealthDataTransmission] ⚠️ No health data to transmit');
      console.log('[HealthDataTransmission] ========================================');
      return null;
    }
    
    return await this.transmitHealthData(healthData, token);
  }
}

